import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WcIcon from '@mui/icons-material/Wc';
import CakeIcon from '@mui/icons-material/Cake';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArticleIcon from '@mui/icons-material/Article';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotesIcon from '@mui/icons-material/Notes';
import FlagIcon from '@mui/icons-material/Flag';
import KitchenIcon from '@mui/icons-material/Kitchen';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { threadApi, templateApi, getStatusDisplay, getTypeDisplay, THREAD_STATUS } from '../services/api';
import RecipeDetailModal from '../components/RecipeDetailModal';

// Format timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Safely render a value that might be an object
const safeString = (val) => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    // Handle common patterns: { text: "..." }, { message: "..." }, { body: "..." }
    if (val.text) return String(val.text);
    if (val.message) return String(val.message);
    if (val.body) return String(val.body);
    try { return JSON.stringify(val); } catch { return '[Object]'; }
  }
  return String(val);
};

// Deep-sanitize an object so no plain object values can crash React rendering.
// Arrays are kept (they're iterable), but plain object values at leaf positions
// are converted to strings via JSON.stringify. This prevents "Objects are not
// valid as a React child" errors regardless of what the backend sends.
const sanitizeForReact = (obj, depth = 0) => {
  if (obj == null) return obj;
  if (typeof obj !== 'object') return obj;

  // Prevent infinite recursion
  if (depth > 10) return String(obj);

  // Arrays: sanitize each element
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForReact(item, depth + 1));
  }

  // Plain objects: sanitize each value
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value == null || typeof value !== 'object') {
      // Primitives pass through
      result[key] = value;
    } else if (Array.isArray(value)) {
      // Arrays: sanitize elements
      result[key] = value.map(item => sanitizeForReact(item, depth + 1));
    } else {
      // Plain object value — check if it's a "data object" that shouldn't be rendered
      // Keep known structural objects (they have known keys we iterate over)
      // but convert unknown nested objects to JSON strings
      const keys = Object.keys(value);
      // Known structural patterns that we expect as objects:
      const isKnownStructure = keys.some(k =>
        ['id', 'name', 'body', 'subject', 'sender_type', 'created_at', 'first_name'].includes(k)
      );
      if (isKnownStructure) {
        result[key] = sanitizeForReact(value, depth + 1);
      } else {
        // Unknown object (like micronutrients, rda_percentages, etc.)
        // Convert to JSON string so it can never crash React
        try {
          result[key] = JSON.stringify(value);
        } catch {
          result[key] = '[Object]';
        }
      }
    }
  }
  return result;
};

// Message Bubble Component
const MessageBubble = ({ message }) => {
  if (!message) return null;

  const isSystem = message.sender_type === 'SYSTEM';
  const isNutritionist = message.sender_type === 'NUTRITIONIST';
  const isUser = message.sender_type === 'USER';

  let bgcolor = '#f5f5f5';
  let align = 'flex-start';
  let textColor = 'text.primary';

  if (isNutritionist) {
    bgcolor = '#E8F5E9';
    align = 'flex-end';
  } else if (isUser) {
    bgcolor = '#E3F2FD';
  } else if (isSystem) {
    bgcolor = '#FFF3E0';
    textColor = 'text.secondary';
  }

  const bodyText = safeString(message.body);
  const subjectText = safeString(message.subject);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: align,
        mb: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          bgcolor,
          borderRadius: 2,
          p: 2,
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="caption" fontWeight={600} color={textColor}>
            {isSystem ? 'System' : isNutritionist ? 'You' : 'User'}
            {message.message_type && ` - ${typeof message.message_type === 'string' ? message.message_type.replace(/_/g, ' ') : safeString(message.message_type)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" ml={2}>
            {formatTime(message.created_at)}
          </Typography>
        </Box>

        {/* Subject */}
        {subjectText && (
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            {subjectText}
          </Typography>
        )}

        {/* Body */}
        <Typography
          variant="body2"
          color={textColor}
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {bodyText}
        </Typography>
      </Box>
    </Box>
  );
};

// Error Boundary to prevent child crashes from blanking the whole page
class ProfileErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('UserProfileCard render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Card sx={{ p: 2 }}>
          <Alert severity="warning">
            Could not render user profile. Some data may be in an unexpected format.
          </Alert>
        </Card>
      );
    }
    return this.props.children;
  }
}

// Top-level error boundary for the entire ThreadDetail page
class ThreadDetailErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('ThreadDetail render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Something went wrong while loading this thread. Some data may be in an unexpected format.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Error: {this.state.error?.message || 'Unknown error'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => window.location.href = '/queue'}
          >
            Back to Queue
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// User Profile Card Component
const UserProfileCard = ({ userProfile, complexCaseReasons, onRecipeClick }) => {
  if (!userProfile) return null;

  const InfoItem = ({ icon, label, value }) => (
    <Box display="flex" alignItems="flex-start" gap={1} py={0.75}>
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {safeString(value) || 'Not specified'}
        </Typography>
      </Box>
    </Box>
  );

  const ChipList = ({ items, color = 'default' }) => (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {items && items.length > 0 ? (
        items.map((item, idx) => {
          const label = typeof item === 'string' ? item.replace(/_/g, ' ') : (item?.name || item?.label || String(item));
          return (
            <Chip
              key={idx}
              label={label}
              size="small"
              color={color}
              variant="outlined"
            />
          );
        })
      ) : (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      )}
    </Box>
  );

  const SectionHeader = ({ icon, label, color = 'text.secondary' }) => (
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      {icon}
      <Typography variant="caption" fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
    </Box>
  );

  const hasAdditionalInfo = userProfile.additional_preferences || userProfile.additional_notes || userProfile.specific_meal_plan;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {safeString(userProfile.first_name) || 'User'}
            </Typography>
            {userProfile.nutrition_profile && (
              <Typography variant="caption" color="text.secondary">
                {typeof userProfile.nutrition_profile === 'string' ? userProfile.nutrition_profile.replace(/_/g, ' ') : safeString(userProfile.nutrition_profile)}
              </Typography>
            )}
          </Box>
          {userProfile.is_complex_case && (
            <Chip label="Complex Case" size="small" color="warning" icon={<FlagIcon />} sx={{ ml: 'auto' }} />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Complex Case Alert */}
        {complexCaseReasons && complexCaseReasons.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Complex Case Reasons:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {complexCaseReasons.map((reason, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{safeString(reason)}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Demographics */}
        <InfoItem
          icon={<WcIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          label="Gender"
          value={userProfile.gender}
        />
        {userProfile.age && (
          <InfoItem
            icon={<CakeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Age"
            value={userProfile.age}
          />
        )}
        <InfoItem
          icon={<CakeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          label="Age Group"
          value={typeof userProfile.age_group === 'string' ? userProfile.age_group.replace(/_/g, '-') : safeString(userProfile.age_group)}
        />
        {userProfile.menstruation_status && (
          <InfoItem
            icon={<FavoriteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Menstruation Status"
            value={safeString(userProfile.menstruation_status_display) || (typeof userProfile.menstruation_status === 'string' ? userProfile.menstruation_status.replace(/_/g, ' ') : safeString(userProfile.menstruation_status))}
          />
        )}

        <Divider sx={{ my: 2 }} />

        {/* Health Goals */}
        {userProfile.health_goal_display && (
          <Box mb={2}>
            <SectionHeader icon={<FlagIcon sx={{ fontSize: 16, color: 'primary.main' }} />} label="Health Goal" />
            <Typography variant="body2" fontWeight={500}>
              {safeString(userProfile.health_goal_display)}
            </Typography>
          </Box>
        )}

        {/* Health Priorities */}
        <Box mb={2}>
          <SectionHeader icon={<FavoriteIcon sx={{ fontSize: 16, color: 'info.main' }} />} label="Health Priorities" />
          {userProfile.health_priorities_display ? (
            <Typography variant="body2">{safeString(userProfile.health_priorities_display)}</Typography>
          ) : (
            <ChipList items={userProfile.health_priorities} color="info" />
          )}
        </Box>

        {/* Medical Conditions */}
        <Box mb={2}>
          <SectionHeader icon={<LocalHospitalIcon sx={{ fontSize: 16, color: 'error.main' }} />} label="Medical Conditions" />
          {userProfile.medical_conditions_display ? (
            <Typography variant="body2">{safeString(userProfile.medical_conditions_display)}</Typography>
          ) : (
            <ChipList items={userProfile.medical_conditions} color="error" />
          )}
        </Box>

        {/* Allergies */}
        <Box mb={2}>
          <SectionHeader icon={<WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />} label="Allergies" />
          <ChipList items={userProfile.allergies} color="warning" />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Dietary Info */}
        <Box mb={2}>
          <SectionHeader icon={<RestaurantIcon sx={{ fontSize: 16, color: 'success.main' }} />} label="Diet Preferences" />
          <Typography variant="body2">
            {safeString(userProfile.diet_preferences) || 'Not specified'}
          </Typography>
        </Box>

        {userProfile.meal_preference && (
          <InfoItem
            icon={<RestaurantIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Meal Preference"
            value={typeof userProfile.meal_preference === 'string' ? userProfile.meal_preference.replace(/_/g, ' ') : safeString(userProfile.meal_preference)}
          />
        )}

        {userProfile.leftover_preference && (
          <InfoItem
            icon={<KitchenIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Leftover Preference"
            value={typeof userProfile.leftover_preference === 'string' ? userProfile.leftover_preference.replace(/_/g, ' ') : safeString(userProfile.leftover_preference)}
          />
        )}

        {/* Household Context */}
        {(userProfile.people_count || userProfile.monthly_grocery_budget) && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader icon={<HomeIcon sx={{ fontSize: 16, color: 'secondary.main' }} />} label="Household" />
            {userProfile.people_count && (
              <InfoItem
                icon={<PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Household Size"
                value={`${userProfile.people_count} ${userProfile.people_count === 1 ? 'person' : 'people'}`}
              />
            )}
            {userProfile.monthly_grocery_budget && (
              <InfoItem
                icon={<AttachMoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Monthly Budget"
                value={userProfile.monthly_grocery_budget}
              />
            )}
          </>
        )}

        {/* Additional Info */}
        {hasAdditionalInfo && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader icon={<NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />} label="Additional Info" />
            {userProfile.additional_preferences && (
              <InfoItem
                icon={<NotesIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Additional Preferences"
                value={userProfile.additional_preferences}
              />
            )}
            {userProfile.additional_notes && (
              <InfoItem
                icon={<NotesIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Additional Notes"
                value={userProfile.additional_notes}
              />
            )}
            {userProfile.specific_meal_plan && (
              <InfoItem
                icon={<RestaurantIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Specific Meal Plan"
                value={userProfile.specific_meal_plan}
              />
            )}
          </>
        )}

        {/* Current Meal Plan Recipes */}
        {Array.isArray(userProfile.current_meal_plan_recipes) && userProfile.current_meal_plan_recipes.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader icon={<MenuBookIcon sx={{ fontSize: 16, color: 'primary.main' }} />} label="Current Meal Plan" />
            <Box sx={{ m: 0 }}>
              {userProfile.current_meal_plan_recipes.map((recipe, idx) => {
                // Handle both string format (old) and object format (new)
                const isObject = typeof recipe === 'object' && recipe !== null;
                const name = isObject ? safeString(recipe.name) : safeString(recipe);
                const recipeId = isObject ? recipe.id : null;

                // Skip items that have no displayable name (e.g. raw data objects)
                if (!name) return null;

                return (
                  <Box
                    key={recipeId || idx}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      mb: 0.5,
                      ...(recipeId && onRecipeClick ? {
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      } : {}),
                    }}
                    onClick={recipeId && onRecipeClick ? () => onRecipeClick(recipeId) : undefined}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={recipeId && onRecipeClick ? { color: 'primary.main', textDecoration: 'underline', textDecorationStyle: 'dotted' } : {}}
                    >
                      {name}
                    </Typography>
                    {isObject && (
                      <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                        {typeof recipe.calories === 'number' && (
                          <Typography variant="caption" color="text.secondary">{Math.round(recipe.calories)} cal</Typography>
                        )}
                        {typeof recipe.protein === 'number' && (
                          <Typography variant="caption" color="text.secondary">P: {recipe.protein}g</Typography>
                        )}
                        {typeof recipe.carbs === 'number' && (
                          <Typography variant="caption" color="text.secondary">C: {recipe.carbs}g</Typography>
                        )}
                        {typeof recipe.fat === 'number' && (
                          <Typography variant="caption" color="text.secondary">F: {recipe.fat}g</Typography>
                        )}
                        {Array.isArray(recipe.allergens) && recipe.allergens.map((a, i) => (
                          <Chip key={i} label={safeString(a) || 'Unknown'} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

function ThreadDetail() {
  const { referenceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef(null);

  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reply form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [closeAfterSend, setCloseAfterSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Close dialog state
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closing, setClosing] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [loadingTemplateId, setLoadingTemplateId] = useState(null);

  // Recipe detail modal state
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);

  const fetchThread = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await threadApi.getThread(referenceId);
      // Sanitize the entire response to prevent any nested objects from crashing React
      const sanitized = sanitizeForReact(data);
      setThread(sanitized);
    } catch (err) {
      setError('Failed to load thread. Please try again.');
      console.error('Thread fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [referenceId]);

  // Fetch templates - use category-based endpoint (no user_id needed)
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const data = await templateApi.getAll('ASYNC_REVIEW');
      const templateList = data.templates || data || [];
      setTemplates(Array.isArray(templateList) ? templateList : []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Handle template selection - render with reference_id to auto-fill placeholders
  const handleSelectTemplate = async (templateId) => {
    setLoadingTemplateId(templateId);
    try {
      const data = await templateApi.render(templateId, { referenceId: referenceId });
      if (data.subject) setSubject(data.subject);
      const bodyContent = data.rendered_body || data.body || '';
      if (bodyContent) setBody(bodyContent);
      setTemplateMenuAnchor(null);
    } catch (err) {
      console.error('Error rendering template:', err);
    } finally {
      setLoadingTemplateId(null);
    }
  };

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  // Fetch templates when thread loads
  useEffect(() => {
    if (thread) {
      fetchTemplates();
    }
  }, [thread, fetchTemplates]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages]);

  const handleSendReply = async () => {
    if (!body.trim()) return;

    setSending(true);
    setSendError(null);

    try {
      const data = {
        subject: subject.trim() || undefined,
        body: body.trim(),
        close_thread: closeAfterSend,
      };

      await threadApi.replyToThread(referenceId, data);

      // Clear form and refresh
      setSubject('');
      setBody('');
      setCloseAfterSend(false);
      await fetchThread();
    } catch (err) {
      setSendError('Failed to send reply. Please try again.');
      console.error('Reply error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleCloseThread = async () => {
    setClosing(true);
    try {
      await threadApi.closeThread(referenceId, closeReason);
      setCloseDialogOpen(false);
      setCloseReason('');
      await fetchThread();
    } catch (err) {
      console.error('Close thread error:', err);
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/queue')} sx={{ mb: 2 }}>
          Back to Queue
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!thread) {
    return null;
  }

  const statusInfo = getStatusDisplay(thread.status);
  const typeInfo = getTypeDisplay(thread.thread_type);
  const isClosed = thread.status === THREAD_STATUS.CLOSED;
  const canReply = thread.can_nutritionist_reply ?? (!isClosed);
  const isAwaitingUser = thread.status === THREAD_STATUS.AWAITING_USER;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/queue')}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h5" fontWeight={700} color="secondary.dark">
              {safeString(thread.user_first_name) || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {safeString(thread.reference_id)}
            </Typography>
          </Box>
          <Box display="flex" gap={1} mt={0.5}>
            <Chip label={safeString(typeInfo.label) || 'Unknown'} size="small" color={typeInfo.color || 'default'} variant="outlined" />
            <Chip label={safeString(statusInfo.label) || 'Unknown'} size="small" color={statusInfo.color || 'default'} />
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchThread}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {!isClosed && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => setCloseDialogOpen(true)}
              size="small"
            >
              Close Thread
            </Button>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box display="flex" gap={3} flexDirection={isMobile ? 'column' : 'row'}>
        {/* User Profile - Sidebar on desktop, top on mobile */}
        <Box sx={{ width: isMobile ? '100%' : 320, flexShrink: 0 }}>
          <ProfileErrorBoundary>
            <UserProfileCard
              userProfile={thread.user_profile || thread.anonymized_profile}
              complexCaseReasons={thread.complex_case_reasons}
              onRecipeClick={(recipeId) => setSelectedRecipeId(recipeId)}
            />
          </ProfileErrorBoundary>
        </Box>

        {/* Messages and Reply */}
        <Box flex={1} display="flex" flexDirection="column" minWidth={0}>
          {/* Messages */}
          <Card sx={{ flex: 1, mb: 2 }}>
            <CardContent sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {Array.isArray(thread.messages) && thread.messages.map((message, idx) => (
                <MessageBubble key={message?.id || idx} message={message} />
              ))}
              <div ref={messagesEndRef} />
            </CardContent>
          </Card>

          {/* Reply Form */}
          {canReply ? (
            <>
            {isAwaitingUser && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Waiting for user response. You can still send a follow-up message.
              </Alert>
            )}
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Send Reply
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={templatesLoading ? <CircularProgress size={16} /> : <ArticleIcon />}
                    onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
                    disabled={templatesLoading || templates.length === 0}
                  >
                    Use Template
                  </Button>
                </Box>

                {/* Template Selection Menu */}
                <Menu
                  anchorEl={templateMenuAnchor}
                  open={Boolean(templateMenuAnchor)}
                  onClose={() => setTemplateMenuAnchor(null)}
                  PaperProps={{
                    sx: { maxHeight: 400, width: 320 }
                  }}
                >
                  {templates.map((template) => (
                    <MenuItem
                      key={template.id}
                      onClick={() => handleSelectTemplate(template.id)}
                      disabled={loadingTemplateId === template.id}
                    >
                      {loadingTemplateId === template.id ? (
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                      ) : null}
                      <ListItemText
                        primary={template.name}
                        secondary={template.category}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </MenuItem>
                  ))}
                  {templates.length === 0 && (
                    <MenuItem disabled>
                      <ListItemText primary="No templates available" />
                    </MenuItem>
                  )}
                </Menu>

                {sendError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {sendError}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Subject (optional)"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Message"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Write your response to the user..."
                  sx={{ mb: 2 }}
                />

                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={closeAfterSend}
                        onChange={(e) => setCloseAfterSend(e.target.checked)}
                      />
                    }
                    label="Close thread after sending"
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleSendReply}
                    disabled={!body.trim() || sending}
                  >
                    {sending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </Box>

                {/* Follow-up info */}
                {thread.max_followups > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    User has used {thread.followups_used} of {thread.max_followups} follow-up replies.
                    {!thread.can_user_reply && ' User cannot send more follow-ups.'}
                  </Typography>
                )}
              </CardContent>
            </Card>
            </>
          ) : isClosed ? (
            <Alert severity="info">This thread is closed.</Alert>
          ) : null}
        </Box>
      </Box>

      {/* Close Thread Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close Thread</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Are you sure you want to close this thread? The user will no longer be able to reply.
          </Typography>
          <TextField
            fullWidth
            label="Reason for closing (optional)"
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCloseThread}
            disabled={closing}
          >
            {closing ? 'Closing...' : 'Close Thread'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipe Detail Modal */}
      <RecipeDetailModal
        open={!!selectedRecipeId}
        onClose={() => setSelectedRecipeId(null)}
        recipeId={selectedRecipeId}
      />
    </Box>
  );
}

// Wrap ThreadDetail in top-level error boundary so the page never goes completely blank
function ThreadDetailWithErrorBoundary() {
  return (
    <ThreadDetailErrorBoundary>
      <ThreadDetail />
    </ThreadDetailErrorBoundary>
  );
}

export default ThreadDetailWithErrorBoundary;
